/**
 * Hijri dates.
 *
 * No library: Intl supports the Umm al-Qura calendar natively, which is
 * the civil calendar used in Saudi Arabia. Adding moment-hijri would cost
 * ~40KB to do the same job less accurately.
 *
 * Statements handed to a Saudi business are expected to carry both
 * calendars; showing only Gregorian reads as foreign software.
 */
const FMT_AR = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
  day: 'numeric', month: 'long', year: 'numeric',
})
const FMT_EN = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', {
  day: 'numeric', month: 'short', year: 'numeric',
})

export function toHijri(iso: string | Date, lang = 'en'): string {
  try {
    const d = typeof iso === 'string' ? new Date(iso + 'T00:00:00') : iso
    if (Number.isNaN(d.getTime())) return ''
    const s = (lang === 'ar' ? FMT_AR : FMT_EN).format(d)
    if (lang === 'ar') return s
    // Newer ICU data already ends the English form with the era ("1448 AH"),
    // older data does not; strip it and add it once, so it never doubles.
    return `${s.replace(/\s*AH$/, '')} AH`
  } catch {
    return ''
  }
}

/** "05 Aug 2026" -- unambiguous, unlike a numeric locale format. */
export function gregorian(iso: string | Date, lang = 'en'): string {
  try {
    const d = typeof iso === 'string' ? new Date(iso + 'T00:00:00') : iso
    if (Number.isNaN(d.getTime())) return String(iso)
    return new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(d)
  } catch {
    return String(iso)
  }
}

/** Both calendars, for a report header. */
export function dualDate(iso: string | Date, lang = 'en'): string {
  const h = toHijri(iso, lang)
  return h ? `${gregorian(iso, lang)}  ·  ${h}` : gregorian(iso, lang)
}

/** "Wed, 28 Oct 2026" -- today's date in the top bar. */
export function gregorianLong(iso: string | Date, lang = 'en'): string {
  try {
    const d = typeof iso === 'string' ? new Date(iso + 'T00:00:00') : iso
    if (Number.isNaN(d.getTime())) return String(iso)
    return new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    }).format(d)
  } catch {
    return String(iso)
  }
}
